import {Logger} from 'neo-common';
import {AutoplayData, Balance, Cashier, FeatureData, GameDataLoaded, Help, History, IPlatforms, Paytable, RoundEnded, RoundStarted, SpinReceived} from './IPlatforms';
import {Platforms} from './Platforms';

export class PlatformMicrogaming implements IPlatforms {

    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    private _requestHistory: {[requestName: string]: MicrogamingData};

    private _isPaused: boolean;

    private _openedDialogs: Array<string>;

    //******************************************************************************************************************
    //  INIT
    //******************************************************************************************************************

    constructor() {
        this._requestHistory = {};
        this._isPaused = false;
        this._openedDialogs = [];
    }

    public create(): void {
        Platforms.canPlay = true;

        if (DEBUG) {
            this.log('create');
        }

        this.createPlugin();
    }

    protected createPlugin(): void {
        if (DEBUG) {
            this.log('createPlugin');
        }

        Platforms.handleOpenCashierPage = true;
        Platforms.handleOpenHistoryPage = true;
        Platforms.handleChangeGame = true;

        window.addEventListener('message', this.handleMessageReceived.bind(this));

        this.sendInitRequest();
    }

    //******************************************************************************************************************
    //  COMUNICATION
    //******************************************************************************************************************

    private sendInitRequest(): void {
        const data: MicrogamingData = {
            Type: 'REQUEST',
            Name: 'bridge_api_ready',
            Origin: 'embeddedgame',
            SenderId: window.name,
            Body: null,
            Id: this.getGUID()
        };

        this.handleMessageSend(data);
    }

    /**
     * Handler for message listener to receive messages from platform.
     * @param event - message event with data from platform
     */
    private handleMessageReceived(event: MessageEvent): void {
        if (event == null || event.data == null) {
            return;
        }

        let data: MicrogamingData;
        if (typeof event.data === 'string') {
            data = JSON.parse(event.data) as MicrogamingData;
        } else {
            data = event.data as MicrogamingData;
        }

        const error = this.validMessage(data);
        if (error != null) {
            data.Error = error;
            this.handleMessageSend(data);
            return;
        }

        if (data.Type.toLowerCase() === 'request') {
            switch (data.Name.toLowerCase()) {
                case "pause_gameplay":
                    this.handlePauseGameplay(data);
                    break;
                case "resume_gameplay":
                    this.handleResumeGameplay(data);
                    break;
            }
        } else if (data.Type.toLowerCase() === 'response') {
            this.handleReponse(data);
        }
    }

    private handleReponse(data: MicrogamingData): void {
        if (this._requestHistory[data.Name.toLowerCase()] == null) {
            //ToDo non recogized reponse ... handle ?
            return;
        }

        if (data.Error != null) {
            switch (data.Error.ErrorCode) {
                case 1201:
                    this.log(`Error 1201 - User already logged out. ${data.Error.Description ? 'Description: ' + data.Error.Description : ''}`);
                    break;
                case 1202:
                    this.log(`Error 1202 - User not logged in. ${data.Error.Description ? 'Description: ' + data.Error.Description : ''}`);
                    break;
                case 1205:
                    this.log(`Error 1205 - Logout failed. ${data.Error.Description ? 'Description: ' + data.Error.Description : ''}`);
                    break;
                case 1301:
                    this.log(`Error 1301 - Cancelled by user or unspported destination. ${data.Error.Description ? 'Description: ' + data.Error.Description : ''}`);
                    break;
                case 1303:
                    this.log(`Error 1303 - Unspported destination. ${data.Error.Description ? 'Description: ' + data.Error.Description : ''}`);
                    break;
            }
        }

        //ToDo handle reponse
        this._requestHistory[data.Name.toLowerCase()] = null;
    }

    private validMessage(data: MicrogamingData): {ErrorCode?: number, Description?: string} {
        if (data == null) {
            return {ErrorCode: 1000};
        }

        const validTypes = ['request', 'response', 'notification'];
        if (validTypes.indexOf(data.Type.toLowerCase()) === -1) {
            return {ErrorCode: 1000, Description: `Unsupported message type: ${data.Type.toLowerCase()}`};
        }

        return null;
    }


    /**
     * Handler to send data from game to platform.
     * @param data - data object send back to platform
     */
    protected handleMessageSend(data: MicrogamingData): void {
        if (data.Type.toLowerCase() === 'request') {
            this._requestHistory[data.Name.toLowerCase()] = data;
        }

        window.top.postMessage(data, '*');
    }

    //******************************************************************************************************************
    //  GAME TO PLATFORM EVENTS
    //******************************************************************************************************************

    public autoplayFinished(args?: AutoplayData): void {
    }

    public autoplayStarted(): void {
    }

    public balanceUpdated(args?: Balance): void {
    }

    public bet(value: number): void {
    }

    public error(code: string, message: string): void {
        if (DEBUG) {
            this.log('error');
        }

        let httpStatus = code.split('-');
        let reason = 106;

        if (httpStatus.length > 0) {
            switch (httpStatus[0]) {
                case '0':
                    reason = 101;
                    break;
                case '401':
                    reason = 104;
                    break;
            }
        }

        const data: MicrogamingData = {
            Type: 'REQUEST',
            Name: 'logout',
            Origin: 'embeddedgame',
            SenderId: window.name,
            Body: {
                "reason": reason
            },
            Id: this.getGUID()
        };

        this.handleMessageSend(data);
    }

    public clientError(id: string, message: string): void {
    }

    public featureFinished(args?: FeatureData): void {
    }

    public featureInit(): void {}

    public featureStarted(args?: FeatureData): void {
    }

    public gameChange(gameCode: string): void {
        if (DEBUG) {
            this.log('gameChange');
        }

        const data: MicrogamingData = {
            Type: 'REQUEST',
            Name: 'launch_game',
            Origin: 'embeddedgame',
            SenderId: window.name,
            Body: {
                "game_id": gameCode
            },
            Id: this.getGUID()
        };

        this.handleMessageSend(data);
    }

    public gameClosing(): void {
        if (DEBUG) {
            this.log('gameClosing');
        }

        const data: MicrogamingData = {
            Type: 'NOTIFICATION',
            Name: 'close_application',
            Origin: 'embeddedgame',
            SenderId: window.name,
            Body: null,
            Id: this.getGUID()
        };

        this.handleMessageSend(data);
    }

    public gameReloading(): void {
        if (DEBUG) {
            this.log('gameReloading');
        }

        const data: MicrogamingData = {
            Type: 'REQUEST',
            Name: 'reload_game',
            Origin: 'embeddedgame',
            SenderId: window.name,
            Body: null,
            Id: this.getGUID()
        };

        this.handleMessageSend(data);
    }

    public gameDataLoaded(args: GameDataLoaded): void {

    }

    public dialogOpened(type: string, isOpen: boolean): void {
        if (DEBUG) {
            this.log('dialogOpened');
        }

        if (isOpen) {
            this._openedDialogs.push(type);
        } else {
            const itemIndex = this._openedDialogs.indexOf(type);
            if (itemIndex === -1) {
                //trying to close dialog that is already closed
                return;
            }

            this._openedDialogs.splice(itemIndex, 1);

            if (this._openedDialogs.length > 0) {
                //wait until all dialogs are closed
                return;
            }
        }

        const data: MicrogamingData = {
            Type: 'NOTIFICATION',
            Name: isOpen ? 'busy' : 'idle',
            Origin: 'embeddedgame',
            SenderId: window.name,
            Body: null,
            Id: this.getGUID()
        };

        this.handleMessageSend(data);
    }

    public fullscreen(enabled: boolean): void {}

    public gameFailed(): void {
    }

    public gameReady(): void {
    }

    public gotoCasier(args?: Cashier): void {
        if (DEBUG) {
            this.log('gotoCasier');
        }

        const data: MicrogamingData = {
            Type: 'REQUEST',
            Name: 'launch_banking',
            Origin: 'embeddedgame',
            SenderId: window.name,
            Body: null,
            Id: this.getGUID()
        };

        this.handleMessageSend(data);
    }

    public gotoHelp(args?: Help): void {
    }

    public gotoHistory(args?: History): void {
        if (DEBUG) {
            this.log('gotoHistory');
        }

        const data: MicrogamingData = {
            Type: 'REQUEST',
            Name: 'launch_game_history',
            Origin: 'embeddedgame',
            SenderId: window.name,
            Body: null,
            Id: this.getGUID()
        };

        this.handleMessageSend(data);
    }

    public gotoPaytable(args?: Paytable): void {
    }

    public loadProgress(progress: number): void {
    }

    public quickSpinEnabled(enabled: boolean): void {
    }

    public roundEnded(args?: RoundEnded): void {
    }

    public roundInit(): void {
    }

    public roundStarted(args?: RoundStarted): void {
    }

    public sendPluginMessage(message: string): void {
    }

    public setVolume(level: number): void {
    }

    public spinEnded(): void {
    }

    public spinReceived(args?: SpinReceived): void {
    }

    public spinStarted(): void {
        const data: MicrogamingData = {
            Type: 'REQUEST',
            Name: 'player_activity',
            Origin: 'embeddedgame',
            SenderId: window.name,
            Body: null,
            Id: this.getGUID()
        };

        this.handleMessageSend(data);
    }

    public userAction(action: string, data: Object): void {
    }

    public win(value: number, cumulative?: boolean): void {}

    //******************************************************************************************************************
    //  PLATFORM TO GAME
    //******************************************************************************************************************

    private handlePauseGameplay(data: MicrogamingData): void {
        if (!this._isPaused) {
            Platforms.gamePlayFreeze();
            this._isPaused = true;
        }

        data.Origin = 'embeddedgame';
        data.SenderId = window.name;
        data.Type = 'RESPONSE';
        this.handleMessageSend(data);
    }

    private handleResumeGameplay(data: MicrogamingData): void {
        if (this._isPaused) {
            Platforms.gamePlayUnfreeze();
            this._isPaused = false;
        }

        data.Origin = 'embeddedgame';
        data.SenderId = window.name;
        data.Type = 'RESPONSE';
        this.handleMessageSend(data);
    }

    //******************************************************************************************************************
    //  HELPER
    //******************************************************************************************************************

    private getGUID(): string {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            var r = Math.random() * 16 | 0,
                v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }

    //******************************************************************************************************************
    //  LOG
    //******************************************************************************************************************

    protected log(s: string): void {
        if (DEBUG) {
            Logger.debugGroup('Playtech', 'Playtech ' + s);
        }
    }
}

export interface MicrogamingData {
    Name: string;
    Body?: Object;
    Type: string;
    Origin: string;
    SenderId: string;
    Id: string
    Error?: {ErrorCode?: number, Description?: string}
}


/** WEBPACK FOOTER **
 ** ./Engine/Platforms/PlatformMicrogaming.ts
 **/